// RoomBoard - Main retro board with real-time card synchronization
// Implements US-2.1: Card operations, US-3.1: Phase transition, US-6.1: Real-time sync, and US-1.4: Room code display

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Column } from '../components/Column';
import { NotesPanel } from '../components/NotesPanel';
import { Card, CardGroup, VoteAllocation } from '../types/card';
import { getTemplate, getTemplateGridClass } from '../config/templates';
import { socketService, ConnectionStatus } from '../services/socketService';
import * as api from '../services/api';
import * as exportService from '../services/exportService';
import { useAuth } from '../contexts/AuthContext';
import insurtaleLogo from '../assets/insurtale-logo.webp';
import styles from '../styles/Board.module.css';
import {
  FileText,
  Target,
  Users,
  Circle,
  Link,
  Check,
  CircleDot,
  Square,
  RotateCcw,
  FileDown,
  Lock,
  Copy,
  X,
  Hourglass,
  AlertTriangle
} from 'lucide-react';

interface Room {
  id: string;
  code: string;
  name: string | null;
  template: string;
  phase: string;
  anonymousMode: boolean;
  facilitator_id: string;
  votingMode: string;
  dotsPerUser: number;
  votingActive: boolean; // US-3.2: Track if voting is active
  exportGenerated: boolean; // US-1.3 & US-4.1: Track if PDF export was generated
  created_at: string;
  closed_at: string | null;
  auto_close_at: string;
}

export default function RoomBoard() {
  const { roomId } = useParams<{ roomId: string }>();
  const location = useLocation();
  const [room, setRoom] = useState<Room | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [groups, setGroups] = useState<CardGroup[]>([]);
  const [participantCount, setParticipantCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [showCopiedNotification, setShowCopiedNotification] = useState(false);

  // US-2.2: Card selection state for grouping
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([]);
  const [isGroupingMode, setIsGroupingMode] = useState(false);

  // GET session ID from localStorage
  const sessionId = localStorage.getItem('elegy_session_id') || '';

  // SSO AUTHENTICATION: Determine facilitator role
  const { isAuthenticated, user } = useAuth();
  const [isFacilitator, setIsFacilitator] = useState(false);

  // API_URL with fallback for single-domain setup
  const API_URL = process.env.REACT_APP_API_URL || '';

  // US-3.2: Voting state
  const [voteAllocation, setVoteAllocation] = useState<VoteAllocation>({
    sessionId,
    dotsUsed: 0,
    dotsRemaining: 0,
  });

  // US-4.1: Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // CHECK if this is a newly created room (from CreateRoom page)
  const isNewRoom = location.state?.isNewRoom || false;
  const [showRoomCode, setShowRoomCode] = useState(isNewRoom);

  /**
   * LOAD room and card data from API
   */
  const loadRoomData = useCallback(async () => {
    if (!roomId || !sessionId) {
      setError('Ungültiger Raum oder Session');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // FETCH room details
      const roomData = await api.getRoomById(roomId);
      setRoom(roomData as any);

      // FETCH existing cards
      const cardsData = await api.fetchCards(roomId, sessionId);
      setCards(cardsData);

      // FETCH existing groups (US-2.2)
      try {
        const groupsResponse = await fetch(`${API_URL}/api/groups/${roomId}`);
        if (groupsResponse.ok) {
          const groupsData = await groupsResponse.json();
          setGroups(groupsData);
        }
      } catch (err) {
        console.error('Error loading groups:', err);
        // NON-FATAL: groups are optional, continue loading
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading room data:', err);
      setError(err instanceof Error ? err.message : 'Raum konnte nicht geladen werden');
      setLoading(false);
    }
  }, [roomId, sessionId]);

  // LOAD room and cards on mount
  useEffect(() => {
    loadRoomData();
  }, [loadRoomData]);

  // DETECT user's role (facilitator or participant)
  useEffect(() => {
    const detectRole = async () => {
      if (!roomId) return;

      try {
        // CHECK if authenticated user is the facilitator of this room
        if (isAuthenticated) {
          const role = await api.getRoomRole(roomId);
          setIsFacilitator(role === 'facilitator');
        } else {
          // UNAUTHENTICATED users are always participants
          setIsFacilitator(false);
        }
      } catch (error) {
        console.error('Failed to detect role:', error);
        setIsFacilitator(false);
      }
    };

    detectRole();
  }, [roomId, isAuthenticated]);

  // SETUP WebSocket connection and event listeners
  useEffect(() => {
    if (!roomId || !sessionId) return;

    // CONNECT to WebSocket
    socketService.connect();

    // JOIN room (pass user ID if authenticated for facilitator detection)
    socketService.joinRoom(roomId, sessionId, user?.id);

    // LISTEN for card events
    socketService.onCardCreated(handleCardCreated);
    socketService.onCardUpdated(handleCardUpdated);
    socketService.onCardDeleted(handleCardDeleted);
    socketService.onParticipantCount(handleParticipantCount);
    socketService.onPhaseChanged(handlePhaseChanged);

    // LISTEN for group events (US-2.2)
    socketService.onGroupCreated(handleGroupCreated);
    socketService.onGroupUpdated(handleGroupUpdated);
    socketService.onGroupDeleted(handleGroupDeleted);
    socketService.onUngroupedAll(handleUngroupedAll);

    // LISTEN for voting events (US-3.2)
    socketService.onVotingStarted(handleVotingStarted);
    socketService.onVotingEnded(handleVotingEnded);
    socketService.onVotingReset(handleVotingReset);
    socketService.onVotingUpdated(handleVotingUpdated);

    // LISTEN for room closed event (US-1.3)
    socketService.onRoomClosed(handleRoomClosed);

    // LISTEN for connection status changes
    const handleConnectionStatus = (status: ConnectionStatus) => {
      setConnectionStatus(status);

      // RECOVER state when reconnected
      if (status === 'connected') {
        loadRoomData();
      }
    };
    socketService.onConnectionStatusChange(handleConnectionStatus);

    // CLEANUP on unmount
    return () => {
      socketService.leaveRoom();
      socketService.off('card:created');
      socketService.off('card:updated');
      socketService.off('card:deleted');
      socketService.off('room:participant_count');
      socketService.off('room:phase_changed');
      socketService.off('group:created');
      socketService.off('group:updated');
      socketService.off('group:deleted');
      socketService.off('group:ungrouped_all');
      socketService.off('voting:started');
      socketService.off('voting:ended');
      socketService.off('voting:reset');
      socketService.off('voting:updated');
      socketService.off('room:closed');
      socketService.offConnectionStatusChange(handleConnectionStatus);
      socketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, sessionId, loadRoomData]);


  /**
   * HANDLE card created event (WebSocket)
   */
  function handleCardCreated(data: { card: Card }) {
    setCards((prev) => {
      // CHECK if card already exists (avoid duplicates)
      if (prev.some((c) => c.id === data.card.id)) {
        return prev;
      }

      // REMOVE any temp card in the same column (optimistic UI cleanup)
      // Temp cards have IDs like "temp-timestamp-random"
      const withoutTempCards = prev.filter((c) => {
        const isTempCard = c.id.startsWith('temp-');
        const isSameColumn = c.column === data.card.column;
        // Keep cards that are NOT temp cards in the same column
        return !(isTempCard && isSameColumn);
      });

      return [...withoutTempCards, data.card];
    });
  }

  /**
   * HANDLE card updated event (WebSocket)
   */
  function handleCardUpdated(data: { card: Card }) {
    setCards((prev) =>
      prev.map((c) => (c.id === data.card.id ? data.card : c))
    );
  }

  /**
   * HANDLE card deleted event (WebSocket)
   */
  function handleCardDeleted(data: { cardId: string }) {
    setCards((prev) => prev.filter((c) => c.id !== data.cardId));
  }

  /**
   * HANDLE participant count update (WebSocket)
   */
  function handleParticipantCount(data: { count: number }) {
    setParticipantCount(data.count);
  }

  /**
   * HANDLE phase change (WebSocket)
   */
  function handlePhaseChanged(data: { phase: string; room: any }) {
    console.log('Phase changed to:', data.phase);
    setRoom((prevRoom) => prevRoom ? { ...prevRoom, phase: data.phase } : null);
  }

  /**
   * HANDLE room closed event (WebSocket, US-1.3)
   */
  function handleRoomClosed(data: { id: string; code: string; name: string; wasExported: boolean; closedAt: Date }) {
    console.log('Room closed:', data.code);

    // SHOW confirmation message
    alert(
      `This retrospective has been closed.\n\n` +
      `Room: ${data.name || data.code}\n` +
      (data.wasExported ? `✓ Room was exported before closing` : `⚠️ Room was not exported`) +
      `\n\nYou will be redirected to the home page.`
    );

    // REDIRECT to home page
    window.location.href = '/';
  }

  /**
   * START facilitation phase (facilitator only)
   */
  async function handleStartFacilitation() {
    if (!roomId || !isFacilitator) {
      console.error('Cannot start facilitation: not authorized');
      return;
    }

    try {
      // CALL API to start facilitation (authentication via JWT)
      await api.startFacilitation(roomId);

      // UPDATE local room state optimistically
      // Socket event will confirm the change for all clients
      if (room) {
        setRoom({ ...room, phase: 'facilitating' });
      }
    } catch (error) {
      console.error('Failed to start facilitation:', error);
      setError('Facilitation konnte nicht gestartet werden');
    }
  }

  /**
   * CREATE new card (called by Column component)
   * Uses optimistic UI - shows card immediately, removes on error
   */
  function handleCreateCard(columnKey: string, text: string) {
    // GENERATE temporary ID for optimistic card
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const now = new Date();
    const optimisticCard: Card = {
      id: tempId,
      roomId: roomId!,
      column: columnKey,
      text,
      authorSessionId: sessionId,
      authorName: null,
      owner: null,
      dueDate: null,
      groupId: null,
      voteCount: 0,
      discussed: false,
      expanded: false,
      createdAt: now,
      updatedAt: now,
    };

    // SHOW card immediately (optimistic UI)
    setCards((prev) => [...prev, optimisticCard]);

    // EMIT via WebSocket for real-time sync
    socketService.createCard(columnKey, text);

    // NOTE: The server will broadcast the real card, which will replace the temp one
  }

  /**
   * UPDATE card (called by Card component)
   * Uses optimistic UI - shows update immediately, reverts on error
   */
  function handleUpdateCard(cardId: string, text: string) {
    // STORE original card for potential rollback
    const originalCard = cards.find((c) => c.id === cardId);
    if (!originalCard) return;

    // UPDATE card immediately (optimistic UI)
    setCards((prev) =>
      prev.map((c) => (c.id === cardId ? { ...c, text, updatedAt: new Date() } : c))
    );

    // EMIT via WebSocket for real-time sync
    socketService.updateCard(cardId, text);

    // NOTE: The server will broadcast the updated card
  }

  /**
   * DELETE card (called by Card component)
   * Uses optimistic UI - hides card immediately, restores on error
   */
  function handleDeleteCard(cardId: string) {
    // REMOVE card immediately (optimistic UI)
    setCards((prev) => prev.filter((c) => c.id !== cardId));

    // EMIT via WebSocket for real-time sync
    socketService.deleteCard(cardId);

    // NOTE: The server will broadcast the deletion
  }

  /**
   * TOGGLE card expand/collapse (US-2.4)
   * Uses optimistic UI - toggles immediately, syncs via WebSocket
   */
  function handleToggleExpand(cardId: string) {
    // TOGGLE expanded state immediately (optimistic UI)
    setCards((prev) =>
      prev.map((c) =>
        c.id === cardId ? { ...c, expanded: !c.expanded } : c
      )
    );

    // EMIT via WebSocket for real-time sync
    socketService.toggleCardExpand(cardId);

    // NOTE: The server will broadcast the expanded state change
  }

  /**
   * TOGGLE card discussed status (US-3.3)
   * Only facilitator can mark cards as discussed during facilitation phase
   */
  function handleToggleDiscussed(cardId: string) {
    if (!isFacilitator || isCollecting) {
      console.warn('Only facilitator can mark cards as discussed during facilitation');
      return;
    }

    // TOGGLE discussed state immediately (optimistic UI)
    setCards((prev) =>
      prev.map((c) =>
        c.id === cardId ? { ...c, discussed: !c.discussed } : c
      )
    );

    // EMIT via WebSocket for real-time sync
    socketService.toggleCardDiscussed(cardId);

    // NOTE: The server will broadcast the discussed state change
  }

  /**
   * MOVE card between columns (drag-and-drop, facilitator only during facilitation)
   */
  function handleMoveCard(cardId: string, targetColumn: string) {
    if (!isFacilitator || isCollecting) {
      console.warn('Only facilitator can move cards during facilitation');
      return;
    }

    // UPDATE card column immediately (optimistic UI)
    setCards((prev) =>
      prev.map((c) =>
        c.id === cardId ? { ...c, column: targetColumn, groupId: null } : c
      )
    );

    // EMIT via WebSocket for real-time sync
    socketService.moveCard(cardId, targetColumn);

    // NOTE: The server will broadcast the card update
  }

  // ========== ACTION ITEM HANDLERS (US-2.3) ==========

  /**
   * CREATE action item (facilitator only, facilitation phase)
   * No optimistic UI to avoid duplicates (action items are less frequent)
   */
  function handleCreateActionItem(text: string, owner?: string, dueDate?: string) {
    if (!isFacilitator || isCollecting) {
      console.warn('Only facilitator can create action items during facilitation');
      return;
    }

    // EMIT via WebSocket for real-time sync
    // The server will broadcast the action item to all clients
    socketService.createActionItem(text, owner, dueDate);
  }

  // ========== NOTE HANDLERS (US-2.5) ==========

  /**
   * CREATE note (facilitator only)
   * Notes use the "notes" column in the cards table
   */
  function handleCreateNote(text: string) {
    if (!isFacilitator) {
      console.warn('Only facilitator can create notes');
      return;
    }

    // EMIT via WebSocket for real-time sync (reuses card infrastructure)
    socketService.createCard('notes', text);
  }

  /**
   * UPDATE note (facilitator only)
   */
  function handleUpdateNote(noteId: string, text: string) {
    if (!isFacilitator) {
      console.warn('Only facilitator can update notes');
      return;
    }

    // EMIT via WebSocket for real-time sync
    socketService.updateCard(noteId, text);
  }

  /**
   * DELETE note (facilitator only)
   */
  function handleDeleteNote(noteId: string) {
    if (!isFacilitator) {
      console.warn('Only facilitator can delete notes');
      return;
    }

    // EMIT via WebSocket for real-time sync
    socketService.deleteCard(noteId);
  }

  // ========== GROUP HANDLERS (US-2.2) ==========

  /**
   * HANDLE group created event (WebSocket)
   */
  function handleGroupCreated(data: { group: CardGroup }) {
    setGroups((prev) => [...prev, data.group]);

    // UPDATE cards to reflect group membership
    if (data.group.cards) {
      setCards((prev) =>
        prev.map((c) =>
          data.group.cards!.some((gc) => gc.id === c.id)
            ? { ...c, groupId: data.group.id }
            : c
        )
      );
    }
  }

  /**
   * HANDLE group updated event (WebSocket)
   */
  function handleGroupUpdated(data: { group: CardGroup }) {
    setGroups((prev) =>
      prev.map((g) => (g.id === data.group.id ? data.group : g))
    );
  }

  /**
   * HANDLE group deleted event (WebSocket)
   */
  function handleGroupDeleted(data: { groupId: string }) {
    setGroups((prev) => prev.filter((g) => g.id !== data.groupId));

    // UPDATE cards to remove group membership
    setCards((prev) =>
      prev.map((c) => (c.groupId === data.groupId ? { ...c, groupId: null } : c))
    );
  }

  /**
   * HANDLE ungroup all event (WebSocket)
   */
  function handleUngroupedAll() {
    setGroups([]);
    setCards((prev) => prev.map((c) => ({ ...c, groupId: null })));
    setSelectedCardIds([]);
    setIsGroupingMode(false);
  }

  /**
   * TOGGLE card selection for grouping (facilitator only)
   */
  function handleToggleSelectCard(cardId: string) {
    setSelectedCardIds((prev) =>
      prev.includes(cardId)
        ? prev.filter((id) => id !== cardId)
        : [...prev, cardId]
    );
  }

  /**
   * CREATE group from selected cards
   */
  function handleGroupSelected() {
    if (selectedCardIds.length < 2) {
      alert('Please select at least 2 cards to group');
      return;
    }

    // VERIFY all selected cards are in same column
    const selectedCards = cards.filter((c) => selectedCardIds.includes(c.id));
    const columns = new Set(selectedCards.map((c) => c.column));
    if (columns.size > 1) {
      alert('All selected cards must be in the same column');
      return;
    }

    const column = selectedCards[0].column;

    // CREATE group via WebSocket
    socketService.createGroup(column, selectedCardIds);

    // CLEAR selection
    setSelectedCardIds([]);
    setIsGroupingMode(false);
  }

  /**
   * UPDATE group label
   */
  function handleUpdateGroupLabel(groupId: string, label: string) {
    socketService.updateGroupLabel(groupId, label);
  }

  /**
   * REMOVE card from group
   */
  function handleUngroupCard(cardId: string) {
    socketService.removeCardFromGroup(cardId);
  }

  /**
   * DELETE entire group
   */
  function handleDeleteGroup(groupId: string) {
    socketService.deleteGroup(groupId);
  }

  /**
   * UNGROUP all cards in room
   */
  function handleUngroupAll() {
    if (!roomId) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Remove all groupings? Cards will not be deleted.')) return;
    socketService.ungroupAll(roomId);
  }

  /**
   * TOGGLE grouping mode
   */
  function handleToggleGroupingMode() {
    setIsGroupingMode((prev) => !prev);
    setSelectedCardIds([]);
  }

  // ========== VOTING HANDLERS (US-3.2) ==========

  /**
   * FETCH vote allocation for current session
   */
  const loadVoteAllocation = useCallback(async () => {
    if (!roomId || !sessionId) return;

    try {
      const allocation = await api.getVoteAllocation(roomId, sessionId);
      setVoteAllocation(allocation);
    } catch (err) {
      console.error('Error loading vote allocation:', err);
    }
  }, [roomId, sessionId]);

  /**
   * LOAD vote allocation when voting starts or on mount
   */
  useEffect(() => {
    if (room?.votingActive) {
      loadVoteAllocation();
    }
  }, [room?.votingActive, loadVoteAllocation]);

  /**
   * HANDLE voting started event (WebSocket)
   */
  function handleVotingStarted() {
    setRoom((prev) => prev ? { ...prev, votingActive: true } : null);
    loadVoteAllocation();
  }

  /**
   * HANDLE voting ended event (WebSocket)
   */
  function handleVotingEnded() {
    setRoom((prev) => prev ? { ...prev, votingActive: false } : null);
  }

  /**
   * HANDLE voting reset event (WebSocket)
   */
  function handleVotingReset() {
    // RESET all vote counts to 0
    setCards((prev) => prev.map((c) => ({ ...c, voteCount: 0 })));
    loadVoteAllocation();
  }

  /**
   * HANDLE vote updated event (WebSocket)
   */
  function handleVotingUpdated(data: { cardId: string; voteCount: number }) {
    // UPDATE card vote count
    setCards((prev) =>
      prev.map((c) => (c.id === data.cardId ? { ...c, voteCount: data.voteCount } : c))
    );
    // REFRESH allocation
    loadVoteAllocation();
  }

  /**
   * START voting (facilitator only)
   */
  function handleStartVoting() {
    if (!roomId || !isFacilitator) return;
    socketService.startVoting(roomId);
  }

  /**
   * END voting (facilitator only)
   */
  function handleEndVoting() {
    if (!roomId || !isFacilitator) return;
    socketService.endVoting(roomId);
  }

  /**
   * RESET voting (facilitator only)
   */
  function handleResetVoting() {
    if (!roomId || !isFacilitator) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Alle Abstimmungen zurücksetzen? Dies löscht alle Stimmen und ermöglicht einen Neustart der Abstimmung.')) return;
    socketService.resetVoting(roomId);
  }

  /**
   * CAST vote on a card
   */
  function handleVote(cardId: string, dots: number) {
    if (!room?.votingActive) return;

    // OPTIMISTIC UI: Update vote count immediately
    setCards((prev) =>
      prev.map((c) =>
        c.id === cardId ? { ...c, voteCount: c.voteCount + dots } : c
      )
    );

    // UPDATE allocation optimistically
    setVoteAllocation((prev) => ({
      ...prev,
      dotsUsed: prev.dotsUsed + dots,
      dotsRemaining: prev.dotsRemaining - dots,
    }));

    // EMIT vote via WebSocket
    socketService.castVote(cardId, dots);
  }

  // ========== EXPORT HANDLERS (US-4.1) ==========

  /**
   * EXPORT retrospective as PDF (facilitator only)
   */
  async function handleExportPDF() {
    if (!roomId || !isFacilitator || !room) {
      console.error('Cannot export: not authorized or invalid room');
      return;
    }

    setIsExporting(true);
    setExportError(null);

    try {
      // PREPARE export data
      const exportData = {
        room: {
          id: room.id,
          code: room.code,
          name: room.name,
          template: room.template,
          facilitatorId: room.facilitator_id,
          anonymousMode: room.anonymousMode,
          votingMode: room.votingMode,
          dotsPerUser: room.dotsPerUser,
          votingActive: room.votingActive,
          phase: room.phase,
          createdAt: new Date(room.created_at),
          closedAt: room.closed_at ? new Date(room.closed_at) : null,
          autoCloseAt: new Date(room.auto_close_at),
          exportGenerated: false,
          exportedAt: null, // US-1.3 & US-4.1: Will be set after export completes
        },
        template: template!,
        cards,
        groups,
        participantCount,
      };

      // GENERATE PDF (client-side)
      const pdfBlob = await exportService.generatePDF(exportData);

      // GENERATE filename
      const filename = exportService.generateFilename(room as any, template!);

      // DOWNLOAD PDF to user's device
      exportService.downloadPDF(pdfBlob, filename);

      // MARK export as generated (call backend API with JWT auth)
      if (roomId) {
        await api.markExportGenerated(roomId);

        // UPDATE local room state to reflect export was generated
        setRoom((prev) => prev ? { ...prev, exportGenerated: true } : null);
      }

      console.log('PDF exported successfully:', filename);
    } catch (err) {
      console.error('Export failed:', err);
      setExportError(err instanceof Error ? err.message : 'PDF konnte nicht exportiert werden');
    } finally {
      setIsExporting(false);
    }
  }

  // ========== ROOM LIFECYCLE HANDLERS (US-1.3) ==========

  /**
   * CLOSE room and destroy all data (facilitator only)
   */
  function handleCloseRoom() {
    if (!roomId || !isFacilitator || !room) {
      console.error('Cannot close room: not authorized');
      return;
    }

    // CHECK if export exists
    const hasExport = room.exportGenerated || false;

    // BUILD confirmation message
    let message = 'Diesen Retro-Raum schließen? Dies löscht unwiderruflich alle Karten, Abstimmungen und Gruppierungen.';
    if (!hasExport) {
      message = 'Diese Retro wurde noch nicht exportiert!\n\n' + message + '\n\nExportiere sie zuerst (Als PDF exportieren Button).';
    }

    // CONFIRM with user
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(message)) return;

    // DELETE room via API (authentication via JWT)
    fetch(`${API_URL}/api/rooms/${roomId}/close`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('elegy_auth_token')}`,
      },
    })
      .then(() => {
        // REDIRECT to home page after successful close
        window.location.href = '/';
      })
      .catch((error) => {
        console.error('Failed to close room:', error);
        setError('Raum konnte nicht geschlossen werden');
      });
  }

  // RENDER loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p className={styles.loadingText}>Retro-Board wird geladen...</p>
      </div>
    );
  }

  // RENDER error state
  if (error || !room) {
    return (
      <div className={styles.errorContainer}>
        <h2 className={styles.errorMessage}>Raum konnte nicht geladen werden</h2>
        <p className={styles.errorDetails}>{error || 'Raum nicht gefunden'}</p>
      </div>
    );
  }

  // GET template configuration
  const template = getTemplate(room.template);
  if (!template) {
    return (
      <div className={styles.errorContainer}>
        <h2 className={styles.errorMessage}>Ungültiges Template</h2>
        <p className={styles.errorDetails}>
          Template "{room.template}" nicht gefunden
        </p>
      </div>
    );
  }

  const gridClass = getTemplateGridClass(room.template);
  const isCollecting = room.phase === 'collecting';

  /**
   * COPY room code to clipboard
   */
  function handleCopyCode() {
    if (!room?.code) return;

    navigator.clipboard.writeText(room.code).then(() => {
      setShowCopiedNotification(true);
      setTimeout(() => setShowCopiedNotification(false), 2000);
    }).catch((err) => {
      console.error('Failed to copy room code:', err);
    });
  }

  // RENDER board
  return (
    <div className={styles.boardContainer}>
      {/* CONNECTION STATUS INDICATOR */}
      {connectionStatus === 'reconnecting' && (
        <div className={styles.connectionBanner}>
          <RotateCcw size={16} className={styles.reconnectingIcon} />
          Verbindung wird wiederhergestellt...
        </div>
      )}
      {connectionStatus === 'disconnected' && (
        <div className={`${styles.connectionBanner} ${styles.disconnected}`}>
          <AlertTriangle size={16} />
          Verbindung verloren. Versuche Neuverbindung...
        </div>
      )}
      {connectionStatus === 'error' && (
        <div className={`${styles.connectionBanner} ${styles.error}`}>
          <X size={16} />
          Verbindung fehlgeschlagen. Bitte Seite neu laden.
        </div>
      )}

      {/* ROOM CODE BANNER (for newly created rooms) */}
      {showRoomCode && room && (
        <div className={styles.roomCodeBanner}>
          <div className={styles.roomCodeContent}>
            <span className={styles.roomCodeLabel}>Teile diesen Code mit deinem Team:</span>
            <div className={styles.roomCodeBox}>
              <span className={styles.roomCodeText}>{room.code}</span>
              <button
                className={styles.copyButton}
                onClick={handleCopyCode}
                aria-label="Copy room code"
              >
                {showCopiedNotification ? <><Check size={16} /> Kopiert!</> : <><Copy size={16} /> Kopieren</>}
              </button>
            </div>
            <button
              className={styles.dismissButton}
              onClick={() => setShowRoomCode(false)}
              aria-label="Dismiss room code banner"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* HEADER CARD */}
      <div className={styles.elevatedCard}>
        <div className={styles.boardHeader}>
        <h1 className={styles.boardTitle}>
          <img src={insurtaleLogo} alt="insur_tale" className={styles.boardLogo} />
          {room.name || template.name}
        </h1>
        <div className={styles.boardMeta}>
          <span className={styles.phaseIndicator}>
            {room.phase === 'collecting' ? (
              <><FileText size={16} /> Sammeln</>
            ) : (
              <><Target size={16} /> Facilitieren</>
            )}
          </span>
          <span className={styles.participantCount}>
            <Users size={16} /> {participantCount} Teilnehmer
          </span>
          {connectionStatus === 'connected' && (
            <Circle size={10} fill="currentColor" className={styles.connectedIndicator} />
          )}
        </div>
        </div>
      </div>

      {/* CONTENT CARD */}
      <div className={styles.elevatedCard}>
        {/* FACILITATOR CONTROLS */}
        {isFacilitator && (
        <div className={styles.facilitatorControls}>
          {/* START FACILITATION BUTTON (facilitator only, collecting phase only) */}
          {isFacilitator && isCollecting && (
            <button
              className={styles.startFacilitationButton}
              onClick={handleStartFacilitation}
            >
              Retro starten
            </button>
          )}

          {/* GROUPING CONTROLS (facilitator only, facilitation phase only) */}
          {isFacilitator && !isCollecting && (
            <>
              <button
                className={`${styles.groupingModeButton} ${isGroupingMode ? styles.active : ''}`}
                onClick={handleToggleGroupingMode}
              >
                {isGroupingMode ? <><Check size={16} /> Gruppierungs-Modus</> : <><Link size={16} /> Karten gruppieren</>}
              </button>

              {isGroupingMode && selectedCardIds.length >= 2 && (
                <button
                  className={styles.groupSelectedButton}
                  onClick={handleGroupSelected}
                >
                  Auswahl gruppieren ({selectedCardIds.length})
                </button>
              )}

              {groups.length > 0 && (
                <button
                  className={styles.ungroupAllButton}
                  onClick={handleUngroupAll}
                >
                  Alle Gruppierungen auflösen
                </button>
              )}
            </>
          )}

          {/* VOTING CONTROLS (facilitator only, facilitation phase only, voting enabled) - US-3.2 */}
          {isFacilitator && !isCollecting && room.votingMode === 'dot_voting' && (
            <>
              {!room.votingActive ? (
                <button
                  className={styles.startVotingButton}
                  onClick={handleStartVoting}
                >
                  <CircleDot size={16} /> Abstimmung starten
                </button>
              ) : (
                <>
                  <button
                    className={styles.endVotingButton}
                    onClick={handleEndVoting}
                  >
                    <Square size={16} /> Abstimmung beenden
                  </button>
                  <button
                    className={styles.resetVotingButton}
                    onClick={handleResetVoting}
                  >
                    <RotateCcw size={16} /> Abstimmung zurücksetzen
                  </button>
                </>
              )}
            </>
          )}

          {/* EXPORT PDF BUTTON (facilitator only, facilitation phase only) - US-4.1 */}
          {isFacilitator && !isCollecting && (
            <button
              className={styles.exportButton}
              onClick={handleExportPDF}
              disabled={isExporting}
            >
              {isExporting ? <><Hourglass size={16} /> Exportiere...</> : <><FileDown size={16} /> Als PDF exportieren</>}
            </button>
          )}

          {/* CLOSE ROOM BUTTON (facilitator only, facilitation phase only) - US-1.3 */}
          {isFacilitator && !isCollecting && (
            <button
              className={styles.closeRoomButton}
              onClick={handleCloseRoom}
            >
              <Lock size={16} /> Raum schließen
            </button>
          )}
        </div>
        )}

        {/* EXPORT ERROR MESSAGE */}
        {exportError && (
          <div className={styles.exportError}>
            <X size={16} /> Export fehlgeschlagen: {exportError}
          </div>
        )}

        {/* VOTING STATUS INDICATOR (show when voting active) - US-3.2 */}
        {room.votingActive && (
          <div className={styles.votingStatus}>
            <span className={styles.votingIndicator}><CircleDot size={16} /> Abstimmung aktiv</span>
            <span className={styles.dotsRemaining}>
              {voteAllocation.dotsRemaining} / {room.dotsPerUser} Punkte übrig
            </span>
          </div>
        )}

        <div className={styles.boardContent}>
        <div
          className={`${styles.boardGrid} ${styles[gridClass]}`}
          data-export-board
        >
          {template.columns.map((column) => (
            <Column
              key={column.key}
              column={column}
              cards={cards}
              groups={groups}
              sessionId={sessionId}
              isCollecting={isCollecting}
              isFacilitator={isFacilitator}
              onCreateCard={handleCreateCard}
              onUpdateCard={handleUpdateCard}
              onDeleteCard={handleDeleteCard}
              onToggleExpand={handleToggleExpand}
              onToggleDiscussed={handleToggleDiscussed}
              onCreateActionItem={handleCreateActionItem}
              selectedCardIds={selectedCardIds}
              onToggleSelectCard={handleToggleSelectCard}
              showCardSelect={isGroupingMode && isFacilitator && !isCollecting}
              onUpdateGroupLabel={handleUpdateGroupLabel}
              onUngroupCard={handleUngroupCard}
              onDeleteGroup={handleDeleteGroup}
              votingActive={room.votingActive}
              dotsRemaining={voteAllocation.dotsRemaining}
              onVote={handleVote}
              onMoveCard={handleMoveCard}
            />
          ))}
        </div>
        </div>
      </div>

      {/* NOTES PANEL (US-2.5) - Floating facilitator notes */}
      <NotesPanel
        notes={cards.filter((card) => card.column === 'notes')}
        sessionId={sessionId}
        isFacilitator={isFacilitator}
        onCreateNote={handleCreateNote}
        onUpdateNote={handleUpdateNote}
        onDeleteNote={handleDeleteNote}
      />
    </div>
  );
}
